import { create } from "zustand";
import type { AuthUser } from "../lib/auth/msal";
import type { LensUser, LensSettings } from "../lib/fs/config";
import { savePrefs } from "../lib/fs/prefs";

export type Lang = "en" | "nl";
export type Theme = "light" | "dark";

export type AuthStatus =
  | "signed_out"
  | "signing_in"
  | "signed_in"
  | "unknown_user";

export type LlmStatus = "idle" | "ok" | "quota" | "error";

// ── AppState ───────────────────────────────────────────────────────────────
// Navigation is URL-based (wouter). The store holds session/user data and
// cross-cutting context (accessToken, active consultant, active workflow)
// but not tab machinery. See App.tsx for the route table.

interface AppState {
  language: Lang;
  setLanguage: (lang: Lang, persist?: boolean) => void;

  theme: Theme;
  setTheme: (theme: Theme) => void;

  authStatus: AuthStatus;
  user: AuthUser | null;
  lensUser: LensUser | null;
  repoPath: string | null;
  accessToken: string | null;
  setAuth: (
    status: AuthStatus,
    details?: {
      user?: AuthUser | null;
      lensUser?: LensUser | null;
      repoPath?: string | null;
      accessToken?: string | null;
    },
  ) => void;

  hasMasterCv: boolean | null;
  setHasMasterCv: (has: boolean | null) => void;

  lastLoginTime: string | null;
  setLastLoginTime: (time: string | null) => void;

  settings: LensSettings | null;
  setSettings: (settings: LensSettings | null) => void;

  // Cross-component context. Populated by routes when appropriate — e.g.
  // /consultants/:folder writes activeCandidateFolder on mount so screens
  // that navigate to /consolidation (which has no folder param of its own)
  // can pick up where the dossier left off.
  activeCandidateFolder: string | null;
  setActiveCandidateFolder: (folder: string | null) => void;

  activeWorkflowId: string | null;
  setActiveWorkflowId: (id: string | null) => void;

  activeLlmId: string;
  setActiveLlm: (id: string) => void;

  llmStatus: Record<string, LlmStatus>;
  setLlmStatus: (id: string, status: LlmStatus) => void;
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useAppStore = create<AppState>((set) => ({
  language: "nl",
  setLanguage: (language, persist = true) => {
    set({ language });
    if (persist) void savePrefs({ language });
  },

  theme: "light",
  setTheme: (theme) => {
    set({ theme });
    void savePrefs({ theme });
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },

  authStatus: "signed_out",
  user: null,
  lensUser: null,
  repoPath: null,
  accessToken: null,
  setAuth: (authStatus, details = {}) =>
    set({
      authStatus,
      user: details.user ?? null,
      lensUser: details.lensUser ?? null,
      repoPath: details.repoPath ?? null,
      accessToken: details.accessToken ?? null,
    }),

  hasMasterCv: null,
  setHasMasterCv: (hasMasterCv) => set({ hasMasterCv }),

  lastLoginTime: null,
  setLastLoginTime: (lastLoginTime) => set({ lastLoginTime }),

  settings: null,
  setSettings: (settings) => set({ settings }),

  activeCandidateFolder: null,
  setActiveCandidateFolder: (activeCandidateFolder) => set({ activeCandidateFolder }),

  activeWorkflowId: null,
  setActiveWorkflowId: (activeWorkflowId) => set({ activeWorkflowId }),

  activeLlmId: "",
  setActiveLlm: (activeLlmId) => set({ activeLlmId }),

  llmStatus: {},
  setLlmStatus: (id, status) =>
    set((state) => ({ llmStatus: { ...state.llmStatus, [id]: status } })),
}));
